import { useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { ArrowLeft, ArrowRight, Check, ChevronDown, Mail, Menu, X } from "lucide-react";
import { emailHref, filterLabels, formattedEngineMiles, formattedSalePrice, galleryImages, vehicle, type GalleryTag } from "./data";

const featuredOrder = ["IMG_0805", "IMG_0806", "IMG_0887", "IMG_0868", "IMG_0820"];
const featuredImages = featuredOrder.map((id) => galleryImages.find((image) => image.id === id)!);

function ResponsiveImage({
  image,
  sizes,
  loading = "lazy",
  fetchPriority = "auto",
}: {
  image: typeof galleryImages[number];
  sizes: string;
  loading?: "eager" | "lazy";
  fetchPriority?: "high" | "low" | "auto";
}) {
  return <picture>
    <source type="image/webp" srcSet={image.webpSrcSet} sizes={sizes} />
    <img src={image.src} srcSet={image.jpegSrcSet} sizes={sizes} alt={image.alt} loading={loading} fetchPriority={fetchPriority} />
  </picture>;
}

function StickyHeader() {
  const [open, setOpen] = useState(false);
  const links = [
    ["photos", "Photos"],
    ["details", "Details"],
    ["more-information", "More information"],
  ];

  return (
    <header className="site-header">
      <button className="menu-toggle" type="button" aria-label="Toggle navigation" onClick={() => setOpen(!open)}>
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>
      <nav className={open ? "site-nav is-open" : "site-nav"} aria-label="Primary navigation">
        {links.map(([href, label]) => <a key={href} href={`#${href}`} onClick={() => setOpen(false)}>{label}</a>)}
      </nav>
      <a className="header-enquire" href="#enquire" onClick={() => setOpen(false)}>Enquire</a>
    </header>
  );
}

function HeroCarousel({ onOpen }: { onOpen: (index: number) => void }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [active, setActive] = useState(0);

  const move = (direction: "previous" | "next") => {
    if (!emblaApi) return;
    direction === "next" ? emblaApi.scrollNext() : emblaApi.scrollPrev();
    setActive(emblaApi.selectedScrollSnap());
  };

  return <div className="hero-carousel">
    <div className="embla" ref={emblaRef}>
      <div className="embla-container">
        {featuredImages.map((image, index) => <button className="hero-slide" key={image.id} type="button" onClick={() => onOpen(index)} aria-label={`View ${image.alt}`}>
          <ResponsiveImage image={image} sizes="(max-width: 820px) 100vw, 54vw" loading={index === 0 ? "eager" : "lazy"} fetchPriority={index === 0 ? "high" : "auto"} />
        </button>)}
      </div>
    </div>
    <div className="carousel-controls">
      <button type="button" className="icon-button" onClick={() => move("previous")} aria-label="Previous featured photo"><ArrowLeft size={20} /></button>
      <span>{active + 1} / {featuredImages.length}</span>
      <button type="button" className="icon-button" onClick={() => move("next")} aria-label="Next featured photo"><ArrowRight size={20} /></button>
    </div>
  </div>;
}

function Gallery({ onOpen }: { onOpen: (index: number) => void }) {
  const [activeFilter, setActiveFilter] = useState<GalleryTag>("all");
  const visibleImages = useMemo(() => activeFilter === "all"
    ? galleryImages
    : galleryImages.filter((image) => image.tags.includes(activeFilter)), [activeFilter]);

  return <section className="section gallery-section" id="photos">
    <div className="section-heading gallery-heading"><div><p className="section-kicker">The photographs</p><h2>Photo gallery</h2></div><span>{galleryImages.length} photos</span></div>
    <div className="filters" aria-label="Filter photographs">
      {filterLabels.map((filter) => <button key={filter.value} className={activeFilter === filter.value ? "filter is-active" : "filter"} type="button" onClick={() => setActiveFilter(filter.value)}>{filter.label}{filter.value === "all" ? ` (${galleryImages.length})` : ""}</button>)}
    </div>
    <div className="gallery-grid">
      {visibleImages.map((image, index) => <button key={image.id} className={`gallery-item gallery-item-${index % 5}`} type="button" onClick={() => onOpen(galleryImages.indexOf(image))} aria-label={`View ${image.alt}`}>
        <ResponsiveImage image={image} sizes="(max-width: 700px) 50vw, (max-width: 1100px) 33vw, 300px" />
      </button>)}
    </div>
    <p className="gallery-note">Click any photo to view it larger.</p>
  </section>;
}

function PriceBand() {
  return <section className="price-band section section--tint" aria-label="Asking price">
    <div>
      <p className="section-kicker">Private sale</p>
      <p className="asking-price">{formattedSalePrice}</p>
    </div>
    <p className="price-note">Open to reasonable negotiation with a genuine buyer.</p>
  </section>;
}

function DetailList() {
  return <dl className="facts-list">{vehicle.facts.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>;
}

const includedItems = [
  "Camper conversion with pop-top roof",
  "Rear seating that converts into a bed",
  "Kitchen, sink and fridge/freezer",
  "Leisure battery and mains hookup",
  "Storage cupboards and interior lighting",
  "Drive-away awning and camping equipment",
  "Alpine media system with Apple CarPlay and Android Auto",
];

const replacementWorkItems = [
  "Brand-new, factory-supplied Volkswagen replacement engine",
  "New turbocharger fitted",
  "New injectors",
  "New oil pump",
  "New starter battery",
  "New leisure battery",
  "Electrical checks and associated rewiring",
];

function ReplacementWorkSummary() {
  return <div className="replacement-work">
    <div className="replacement-work-heading">
      <p className="section-kicker">Recent work</p>
      <h3>What has been replaced</h3>
      <p>Each replacement component listed below was purchased new and was not sourced second-hand or from another vehicle.</p>
    </div>
    <ul className="replacement-work-list">
      {replacementWorkItems.map((item) => <li key={item}><Check size={17} />{item}</li>)}
    </ul>
    <p className="mileage-note"><strong>Vehicle odometer:</strong> just over 100,000 miles. <strong>Replacement engine:</strong> approximately {formattedEngineMiles} miles since installation; the vehicle odometer has not been reset.</p>
  </div>;
}

function AccordionSection({ id, title, children }: { id?: string; title: string; children: React.ReactNode }) {
  return <details className="accordion" id={id}><summary>{title}<ChevronDown size={20} /></summary><div className="accordion-content">{children}</div></details>;
}

function EnquiryPanel() {
  return <section className="enquiry-panel" id="enquire"><div><p className="section-kicker">Questions or viewing</p><h2>Enquire about the van</h2><p>If you’re interested or have any questions, please get in touch. I’ll be happy to answer questions and arrange a viewing.</p><p className="enquiry-price-note">{formattedSalePrice} asking price · Reasonable negotiation considered for a genuine buyer.</p></div><a className="button button-primary" href={emailHref}><Mail size={18} /> Email about the van <ArrowRight size={18} /></a></section>;
}

function App() {
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const slides = galleryImages.map((image) => ({ src: image.lightboxSrc, alt: image.alt }));

  return <div id="top">
    <StickyHeader />
    <main>
      <section className="hero section">
        <HeroCarousel onOpen={(index) => setLightboxIndex(index)} />
        <div className="hero-copy"><p className="section-kicker">A cared-for camper conversion</p><h1>{vehicle.title}</h1><p className="lede">A well looked after Volkswagen Transporter converted into a comfortable and practical camper van. Ideal for weekends away or longer trips, with a versatile interior, pop-top roof and awning.</p><div className="hero-actions"><a className="button button-primary" href="#photos">View all photos <ArrowRight size={18} /></a><span>{galleryImages.length} photos</span></div></div>
      </section>

      <Gallery onOpen={(index) => setLightboxIndex(index)} />
      <PriceBand />

      <span id="details" className="section-anchor" aria-hidden="true" />
      <section className="section section--tint details-section">
        <div className="details-column"><p className="section-kicker">The base vehicle</p><h2>Vehicle details</h2><DetailList /></div>
        <div className="details-column included"><p className="section-kicker">Ready for trips</p><h2>Included with the sale</h2><ul>{includedItems.map((item) => <li key={item}><Check size={17} />{item}</li>)}</ul></div>
      </section>

      <span id="more-information" className="section-anchor" aria-hidden="true" />
      <section className="section story-section"><div className="story-copy"><p className="section-kicker">Useful context</p><h2>Recent work</h2><p>The van has had recent mechanical and electrical work, with service and repair paperwork available for serious buyers. The replacement-engine details and exact work carried out will be confirmed from the invoices before publication.</p><p>The photographs show the vehicle honestly, including the minor cosmetic wear expected of a used 2016 van.</p></div><ResponsiveImage image={galleryImages.find((image) => image.id === "IMG_0870")!} sizes="(max-width: 820px) 100vw, 50vw" /><ReplacementWorkSummary /></section>

      <section className="section section--tint more-section"><div><p className="section-kicker">A practical private sale</p><h2>More information</h2><p>This is a comfortable, versatile camper van with a pop-top sleeping area, rear bed, fitted kitchen, storage and camping equipment. The full image collection is here to make it easy to inspect the conversion before arranging a viewing.</p></div><ResponsiveImage image={galleryImages.find((image) => image.id === "IMG_0887")!} sizes="(max-width: 820px) 100vw, 50vw" /></section>

      <section className="mobile-accordions section"><AccordionSection title="Vehicle details"><DetailList /></AccordionSection><AccordionSection title="Included with the sale"><ul>{includedItems.map((item) => <li key={item}><Check size={17} />{item}</li>)}</ul></AccordionSection><AccordionSection title="Recent work"><p>The van has had recent mechanical and electrical work, with service and repair paperwork available for serious buyers.</p><ReplacementWorkSummary /></AccordionSection><AccordionSection title="More information"><p>Questions, additional details and viewing arrangements can be discussed by email.</p></AccordionSection></section>
      <div className="section enquiry-section"><EnquiryPanel /></div>
    </main>
    <footer className="site-footer"><span>Private Sale</span><a href="#top">Back to top ↑</a></footer>
    <Lightbox open={lightboxIndex >= 0} close={() => setLightboxIndex(-1)} index={lightboxIndex} slides={slides} on={{ view: ({ index }) => setLightboxIndex(index) }} />
  </div>;
}

export default App;
